import React, { useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import notify from '~/components/Notify';
import SecurityService from '~/api/services/security';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormFields } from '~/components/FormFields';
import { Accordion, Checkbox, Divider, Form, Header, Icon, Message, Transition } from 'semantic-ui-react';
import RoleCard from './RoleCard';
import { useAuth } from '~/routes/components/AuthProvider';
import { El } from './style';
import Dialog from '~/components/Dialog';

const Security: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false)
  const [securityOptions, setSecurityOptions] = useState(null)
  const [activeAccordians, setActiveAccordians] = useState([])
  const [openForcePasswordModal, setOpenForcePasswordModal] = useState(false)
  const [checkedRoles, setCheckedRoles] = useState([])
  const { fetchSystemData } = useAuth()

  const { handleSubmit, control, reset, watch, getValues } = useForm({
    mode: 'onSubmit'
  })

  const { fields } = useFieldArray({
    control: control,
    name: 'roles'
  })

  const wathcPasswordSecurityField = watch('passwordSecurity')

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setIsBusy(true)
    try {
      const responseOptions = await SecurityService.getSecurityOptions()
      const optionsUsers = responseOptions.data.userTypes
      const responseData = await SecurityService.getSecurityData()
      //change this inside of the form fields checkbox component
      responseData.data.passwordSecurityUsers = optionsUsers.map(userType => responseData.data.passwordSecurityUsers.find(key => key === userType.key) ? userType.key : undefined)
      setSecurityOptions(responseOptions.data)
      reset(responseData.data)
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  async function onSubmit(data) {
    // talk to the backend this should be handled with the api
    reset(data)
    if (data.passwordSecurityUsers) {
      data.passwordSecurityUsers = data.passwordSecurityUsers.filter(item => !!item)
    }
    data.roles.forEach(role => {
      delete role.roleLabel
      delete role.roleSlug
    })
    setIsBusy(true)
    try {
      await SecurityService.updateSecurityData(data)
      fetchSystemData()
      notify(t('Security updated'), 'success')
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  function renderPasswordSecurityForm() {
    return <>
      <El.StyledHeader>{t('Password security')}</El.StyledHeader>
      <FormFields.Checkbox
        label={t('Increase security')}
        name={'passwordSecurity'}
        checkboxValue={1}
        control={control}
      />
      <Message color='yellow' style={{ marginBottom: '1em' }}>
        <Message.Header style={{ display: 'flex', alignItems: 'baseline', marginBottom: 15 }}>
          <Icon name='info circle' color='yellow' />
          <div>{t('If the increased password security is activated, all future users must assign a password that meets the following criteria:')}</div>
        </Message.Header>
        <Message.Content style={{ marginLeft: 20 }}>
          <div>{t('At least 8 characters long')}</div>
          <div>{t('At least one uppercase and lowercase letter')}</div>
          <div>{t('At least one number')}</div>
          {wathcPasswordSecurityField === 1 && <div>{t('At least one special character')}</div>}
        </Message.Content>
      </Message>
      <Form.Group>
        {securityOptions?.userTypes.map((userType, index) => {
          return <FormFields.Checkbox
            key={userType.key}
            name={`passwordSecurityUsers[${index}]`}
            label={userType.label}
            checkboxValue={userType.key}
            control={control}
          />
        })}
      </Form.Group>
      <Form.Field>{t('To force users to resset the password click on the button below:')}</Form.Field>
      <Form.Button
        onClick={() => setOpenForcePasswordModal(true)}
        primary
        type='button'
      >{t('Force restart password')}
      </Form.Button>
    </>
  }

  function handleClick(id) {
    const accordianOpenIndex = activeAccordians.findIndex(activeAccordian => activeAccordian === id)
    if (accordianOpenIndex >= 0) {
      setActiveAccordians(activeAccordians.filter((_, index) => index !== accordianOpenIndex))
    } else {
      setActiveAccordians([...activeAccordians, id])
    }
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'end' })
    }
  }

  function renderTwoFactorForm() {
    return <>
      <Header>{t('Two-Factor-Authentication')}</Header>
      {fields.map((field, index) => {
        const active = activeAccordians.find(activeAccordian => activeAccordian === field['roleId']) !== undefined
        return <El.StyledAccordion exclusive={false} styled key={field['roleId']}>
          <Accordion.Title
            index={index}
            active={active}
            onClick={() => handleClick(field['roleId'])}
          >
            <div>{field['roleLabel']}</div>
            <Icon name={active ? 'minus' : 'plus'} />
          </Accordion.Title>
          <Transition visible={active} animation='slide down' duration={350}>
            <Accordion.Content
              active={active}
              content={<RoleCard
                key={field.id}
                watch={watch}
                getValues={getValues}
                control={control}
                role={field}
                index={index}
              />}
            />
          </Transition>
        </El.StyledAccordion>
      })}
    </>
  }

  function renderContent() {
    return <>
      {renderPasswordSecurityForm()}
      <Divider />
      {renderTwoFactorForm()}
    </>
  }

  function forcePasswordContent() {
    return <div>
      <div>{t('Please note that the passwords for all selected user groups will be reset. The next time they log in, the respective users will be asked to assign a new password.')}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 20, justifyContent: 'center' }}>
        {securityOptions?.userTypes.map((userType, index) => {
          return <Checkbox
            key={userType.key}
            onChange={(e, data) => {
              if (data.checked) {
                setCheckedRoles([...checkedRoles, userType.key])
              } else {
                setCheckedRoles(checkedRoles.filter(role => role !== userType.key))
              }
            }}
            checked={checkedRoles.find(checkedRole => checkedRole === userType.key)}
            label={userType.label}
            checkboxValue={userType.key}
            control={control}
          />
        })}
      </div>
    </div>
  }

  async function handleSubmitForcePasswords() {
    try {
      await SecurityService.forceChangePassword({ forced_password_reset_users: [...checkedRoles] })
      setOpenForcePasswordModal(false)
      setCheckedRoles([])
      notify(t('Successfully reseted password'), 'success')
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  function renderForcePasswordModal() {
    return <Dialog
      handleClose={() => {
        setOpenForcePasswordModal(false)
        setCheckedRoles([])
      }}
      handleSubmit={() => handleSubmitForcePasswords()}
      disableSubmit={checkedRoles.length === 0}
      textHeader={t('Force reset password')}
      textContent={forcePasswordContent()}
      open={openForcePasswordModal}
      textClose={t('Abort')}
      textSubmit={t('Reset passwords')}
      type='abort'
    />
  }

  return <Layout
    isBusy={isBusy}
    segmented
    title={t('Security settings')}
    onSubmit={handleSubmit(onSubmit)}
    action={t('Update')}
  >
    {renderForcePasswordModal()}
    {securityOptions && renderContent()}
  </Layout>;
};

export default Security;
